import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import './AdminDashboard.css';

// Fix marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const towerIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-blue.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const wifiIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const MapClickHandler = ({ onLocationSelect }) => {
  useMapEvents({
    click: (e) => {
      onLocationSelect([e.latlng.lat, e.latlng.lng]);
    },
  });
  return null;
};

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [notification, setNotification] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [passwordResetResult, setPasswordResetResult] = useState(null);

  const [stats, setStats] = useState({
    totalTowers: 0,
    totalWifi: 0,
    totalUsers: 0,
    activeLocations: 0
  });

  const [towers, setTowers] = useState([]);
  const [wifiLocations, setWifiLocations] = useState([]);
  const [users, setUsers] = useState([]);
  const [jenisOptions, setJenisOptions] = useState([]);
  const [operatorOptions, setOperatorOptions] = useState([]);
  const [kecamatanOptions, setKecamatanOptions] = useState([]);
  const [providerOptions, setProviderOptions] = useState([]);

  const mapCenter = [-7.0051, 110.4381]; // Semarang

  useEffect(() => {
    // Temporarily bypass authentication for testing
    // const token = localStorage.getItem('token');
    // const role = localStorage.getItem('role');
    // if (!token || role !== 'admin') {
    //   window.location.href = '/login';
    //   return;
    // }
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const loadData = async () => {
    setLoading(true);
    try {
      // Temporarily bypass authentication for testing
      // const token = localStorage.getItem('token');
      // const headers = { 'Authorization': `Bearer ${token}` };
      const headers = {}; // No auth headers for testing
      
      const [towersRes, wifiRes, usersRes, jenisRes, operatorRes, kecamatanRes, providerRes] = await Promise.all([
        fetch('https://monevmedsos.manggaraibaratkab.go.id/apiwifi.php/towers', { headers }),
        fetch('https://monevmedsos.manggaraibaratkab.go.id/apiwifi.php/wifi', { headers }),
        fetch('https://monevmedsos.manggaraibaratkab.go.id/apiwifi.php/users', { headers }),
        fetch('https://monevmedsos.manggaraibaratkab.go.id/apiwifi.php/jenis', { headers }),
        fetch('https://monevmedsos.manggaraibaratkab.go.id/apiwifi.php/operator', { headers }),
        fetch('https://monevmedsos.manggaraibaratkab.go.id/apiwifi.php/kecamatan', { headers }),
        fetch('https://monevmedsos.manggaraibaratkab.go.id/apiwifi.php/provider', { headers })
      ]);

      const [towersData, wifiData, usersData, jenisData, operatorData, kecamatanData, providerData] = await Promise.all([
        towersRes.ok ? towersRes.json().catch(() => []) : Promise.resolve([]),
        wifiRes.ok ? wifiRes.json().catch(() => []) : Promise.resolve([]),
        usersRes.ok ? usersRes.json().catch(() => []) : Promise.resolve([]),
        jenisRes.ok ? jenisRes.json().catch(() => []) : Promise.resolve([]),
        operatorRes.ok ? operatorRes.json().catch(() => []) : Promise.resolve([]),
        kecamatanRes.ok ? kecamatanRes.json().catch(() => []) : Promise.resolve([]),
        providerRes.ok ? providerRes.json().catch(() => []) : Promise.resolve([])
      ]);

      // Ensure all data is arrays
      const safeTowersData = Array.isArray(towersData) ? towersData : [];
      const safeWifiData = Array.isArray(wifiData) ? wifiData : [];
      const safeUsersData = Array.isArray(usersData) ? usersData : [];
      const safeJenisData = Array.isArray(jenisData) ? jenisData : [];
      const safeOperatorData = Array.isArray(operatorData) ? operatorData : [];
      const safeKecamatanData = Array.isArray(kecamatanData) ? kecamatanData : [];
      const safeProviderData = Array.isArray(providerData) ? providerData : [];

      setTowers(safeTowersData);
      setWifiLocations(safeWifiData);
      setUsers(safeUsersData);
      setJenisOptions(safeJenisData);
      setOperatorOptions(safeOperatorData);
      setKecamatanOptions(safeKecamatanData);
      setProviderOptions(safeProviderData);

      setStats({
        totalTowers: safeTowersData.length,
        totalWifi: safeWifiData.length,
        totalUsers: safeUsersData.length,
        activeLocations: safeTowersData.length + safeWifiData.length
      });
    } catch (error) {
      showNotification('Gagal memuat data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleLocationSelect = (coords) => {
    setSelectedLocation(coords);
    setFormData(prev => ({
      ...prev,
      koordinat_lat: coords[0],
      koordinat_lng: coords[1]
    }));
  };

  const handleResetPassword = (user) => {
    setConfirmDialog({
      title: 'Reset Password',
      message: `Apakah Anda yakin ingin mereset password untuk user "${user.username}"? Password baru akan ditampilkan setelah reset.`,
      onConfirm: async () => {
        setLoading(true);
        try {
          // Temporarily bypass auth for testing
          // const token = localStorage.getItem('token');
          const response = await fetch(`https://monevmedsos.manggaraibaratkab.go.id/apiwifi.php/reset-password/${user.id}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              // 'Authorization': `Bearer ${token}`
            }
          });

          if (response.ok) {
            const data = await response.json();
            setPasswordResetResult({
              username: data.username,
              newPassword: data.new_password
            });
          } else {
            showNotification('Gagal mereset password', 'error');
          }
        } catch (error) {
          showNotification('Terjadi kesalahan', 'error');
        } finally {
          setLoading(false);
        }
      },
      onCancel: () => setConfirmDialog(null)
    });
  };

  const handleLogout = () => {
    // Clear authentication data
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    // Redirect to login page
    window.location.href = '/login';
  };

  const resetForm = () => {
    setShowForm(false);
    setEditing(null);
    setFormData({});
    setSelectedLocation(null);
  };

  const handleAdd = async () => {
    setLoading(true);
    try {
      const endpoint = getEndpointForTab(activeTab);
      // Temporarily bypass auth for testing
      // const token = localStorage.getItem('token');
      const response = await fetch(`https://monevmedsos.manggaraibaratkab.go.id/apiwifi.php${endpoint}`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          // 'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        showNotification('Data berhasil ditambahkan');
        await loadData();
        resetForm();
      } else {
        showNotification('Gagal menambahkan data', 'error');
      }
    } catch (error) {
      showNotification('Terjadi kesalahan', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (item) => {
    setEditing(item);
    setFormData(item);
    setShowForm(true);
    if (item.koordinat_lat && item.koordinat_lng) {
      setSelectedLocation([parseFloat(item.koordinat_lat), parseFloat(item.koordinat_lng)]);
    }
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      const endpoint = getEndpointForTab(activeTab);
      // Temporarily bypass auth for testing
      // const token = localStorage.getItem('token');
      const response = await fetch(`https://monevmedsos.manggaraibaratkab.go.id/apiwifi.php${endpoint}/${editing.id}`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          // 'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        showNotification('Data berhasil diperbarui');
        await loadData();
        resetForm();
      } else {
        showNotification('Gagal memperbarui data', 'error');
      }
    } catch (error) {
      showNotification('Terjadi kesalahan', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    setConfirmDialog({
      message: 'Yakin ingin menghapus data ini?',
      onConfirm: async () => {
        setConfirmDialog(null);
        setLoading(true);
        try {
          const endpoint = getEndpointForTab(activeTab);
          // Temporarily bypass auth for testing
          // const token = localStorage.getItem('token');
          const response = await fetch(`https://monevmedsos.manggaraibaratkab.go.id/apiwifi.php${endpoint}/${id}`, {
            method: 'DELETE',
            headers: {
              // 'Authorization': `Bearer ${token}`
            }
          });

          if (response.ok) {
            showNotification('Data berhasil dihapus');
            await loadData();
          } else {
            showNotification('Gagal menghapus data', 'error');
          }
        } catch (error) {
          showNotification('Terjadi kesalahan', 'error');
        } finally {
          setLoading(false);
        }
      },
      onCancel: () => setConfirmDialog(null)
    });
  };

  const getEndpointForTab = (tab) => {
    const endpoints = {
      towers: '/towers',
      wifi: '/wifi',
      users: '/users',
      jenis: '/jenis',
      operator: '/operator',
      kecamatan: '/kecamatan',
      provider: '/provider'
    };
    return endpoints[tab] || '';
  };

  const filteredTowers = towers.filter(t =>
    t.nama_optel?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.operator?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.lokasi?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredWifi = wifiLocations.filter(w =>
    w.nama?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    w.provider?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    w.lokasi?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredUsers = users.filter(u =>
    u.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.role?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredJenis = jenisOptions.filter(j =>
    j.nama?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredOperator = operatorOptions.filter(o =>
    o.nama?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredKecamatan = kecamatanOptions.filter(k =>
    k.nama?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const navigationItems = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'towers', label: 'Menara', icon: '📡' },
    { id: 'wifi', label: 'WiFi', icon: '📶' },
    { id: 'users', label: 'Pengguna', icon: '👥' },
    { id: 'jenis', label: 'Jenis Menara', icon: '🏢' },
    { id: 'operator', label: 'Operator', icon: '📱' },
    { id: 'kecamatan', label: 'Kecamatan', icon: '📍' }
  ];

  const renderDashboard = () => (
    <div className="dashboard-view">
      <div className="welcome-section">
        <h1>Dashboard Admin</h1>
        <p>Selamat datang di panel admin Koorditel Kabupaten Manggarai Barat</p>
      </div>

      <div className="stats-grid">
        {[
          { 
            icon: '📡', 
            value: stats.totalTowers, 
            label: 'Total Menara', 
            color: '#3b82f6',
            bgColor: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            shadowColor: 'rgba(59, 130, 246, 0.3)'
          },
          { 
            icon: '📶', 
            value: stats.totalWifi, 
            label: 'Lokasi WiFi', 
            color: '#10b981',
            bgColor: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            shadowColor: 'rgba(16, 185, 129, 0.3)'
          },
          { 
            icon: '👥', 
            value: stats.totalUsers, 
            label: 'Pengguna', 
            color: '#f59e0b',
            bgColor: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            shadowColor: 'rgba(245, 158, 11, 0.3)'
          },
          { 
            icon: '🗺️', 
            value: stats.activeLocations, 
            label: 'Lokasi Aktif', 
            color: '#8b5cf6',
            bgColor: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
            shadowColor: 'rgba(139, 92, 246, 0.3)'
          }
        ].map((stat, i) => (
          <div key={i} className="stat-card" style={{ 
            '--card-color': stat.color,
            '--card-bg': stat.bgColor,
            '--card-shadow': stat.shadowColor
          }}>
            <div className="stat-icon">
              <span>{stat.icon}</span>
            </div>
            <div className="stat-info">
              <h3>{stat.value}</h3>
              <p>{stat.label}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="quick-actions-section">
        <h2>Aksi Cepat</h2>
        <div className="action-grid">
          <button className="action-card action-tower" onClick={() => setActiveTab('towers')}>
            <div className="action-icon-wrapper">
              <span className="action-icon">📡</span>
            </div>
            <span className="action-text">Tambah Menara</span>
            <div className="action-glow"></div>
          </button>
          <button className="action-card action-wifi" onClick={() => setActiveTab('wifi')}>
            <div className="action-icon-wrapper">
              <span className="action-icon">📶</span>
            </div>
            <span className="action-text">Tambah WiFi</span>
            <div className="action-glow"></div>
          </button>
          <button className="action-card action-user" onClick={() => setActiveTab('users')}>
            <div className="action-icon-wrapper">
              <span className="action-icon">👥</span>
            </div>
            <span className="action-text">Kelola Pengguna</span>
            <div className="action-glow"></div>
          </button>
          <button className="action-card action-map" onClick={() => setActiveTab('dashboard')}>
            <div className="action-icon-wrapper">
              <span className="action-icon">🗺️</span>
            </div>
            <span className="action-text">Lihat Peta</span>
            <div className="action-glow"></div>
          </button>
        </div>
      </div>
    </div>
  );

  const renderTable = (data, columns, onEdit, onDelete, onResetPassword) => (
    <div className="table-container">
      <table>
        <thead>
          <tr>
            {columns.map((col, i) => (
              <th key={i}>{col.label}</th>
            ))}
            <th>Aksi</th>
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td colSpan={columns.length + 1} className="empty-state">
                Tidak ada data
              </td>
            </tr>
          ) : (
            data.map((item) => (
              <tr key={item.id}>
                {columns.map((col, i) => (
                  <td key={i}>
                    {col.render ? col.render(item) : item[col.key]}
                  </td>
                ))}
                <td>
                  <div className="action-buttons">
                    <button className="btn-icon edit" onClick={() => onEdit(item)} title="Edit">
                      ✏️
                    </button>
                    {onResetPassword && (
                      <button className="btn-icon reset" onClick={() => onResetPassword(item)} title="Reset Password">
                        🔑
                      </button>
                    )}
                    <button className="btn-icon delete" onClick={() => onDelete(item.id)} title="Hapus">
                      🗑️
                    </button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  const renderMapForm = (showMap = true) => (
    <div className="form-modal" onClick={(e) => e.target.className === 'form-modal' && resetForm()}>
      <div className="form-container">
        <div className="form-header">
          <h3>{editing ? 'Edit Data' : 'Tambah Data'}</h3>
          <button className="close-btn" onClick={resetForm}>✕</button>
        </div>

        <div className="form-body">
          {renderFormFields()}

          {showMap && (
            <div className="map-section">
              <label>Pilih Lokasi di Peta</label>
              <p className="hint">Klik pada peta untuk memilih koordinat</p>
              <div className="map-wrapper">
                <MapContainer center={mapCenter} zoom={12} style={{ height: '400px', width: '100%' }}>
                  <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; OpenStreetMap'
                  />
                  <MapClickHandler onLocationSelect={handleLocationSelect} />
                  {activeTab === 'towers' && towers.map(t => (
                    <Marker key={t.id} position={[t.koordinat_lat, t.koordinat_lng]} icon={towerIcon}>
                      <Popup>
                        <strong>{t.nama_optel}</strong><br />
                        {t.lokasi}
                      </Popup>
                    </Marker>
                  ))}
                  {activeTab === 'wifi' && wifiLocations.map(w => (
                    <Marker key={w.id} position={[w.koordinat_lat, w.koordinat_lng]} icon={wifiIcon}>
                      <Popup>
                        <strong>{w.nama}</strong><br />
                        {w.lokasi}
                      </Popup>
                    </Marker>
                  ))}
                  {selectedLocation && (
                    <Marker position={selectedLocation}>
                      <Popup>
                        Lokasi Terpilih<br />
                        {selectedLocation[0].toFixed(6)}, {selectedLocation[1].toFixed(6)}
                      </Popup>
                    </Marker>
                  )}
                </MapContainer>
              </div>
              {selectedLocation && (
                <div className="location-info">
                  ✓ Koordinat: {selectedLocation[0].toFixed(6)}, {selectedLocation[1].toFixed(6)}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="form-footer">
          <button className="btn-secondary" onClick={resetForm}>Batal</button>
          <button className="btn-primary" onClick={editing ? handleSave : handleAdd} disabled={loading}>
            {loading ? '⏳ Menyimpan...' : editing ? '💾 Simpan' : '➕ Tambah'}
          </button>
        </div>
      </div>
    </div>
  );

  const renderFormFields = () => {
    const fields = {
      towers: [
        { label: 'Nama Operator', key: 'nama_optel', type: 'text' },
        { label: 'Jenis Menara', key: 'jenis', type: 'select', options: jenisOptions },
        { label: 'Operator', key: 'operator', type: 'select', options: operatorOptions },
        { label: 'Lokasi', key: 'lokasi', type: 'text' },
        { label: 'Kecamatan', key: 'kecamatan', type: 'select', options: kecamatanOptions },
        { label: 'Tinggi Menara', key: 'tinggi', type: 'text' },
        { label: 'Status', key: 'status', type: 'select', options: [{ nama: 'Aktif' }, { nama: 'Tidak Aktif' }] },
        { label: 'Latitude', key: 'koordinat_lat', type: 'number' },
        { label: 'Longitude', key: 'koordinat_lng', type: 'number' }
      ],
      wifi: [
        { label: 'Nama Lokasi', key: 'nama', type: 'text' },
        { label: 'Lokasi', key: 'lokasi', type: 'text' },
        { label: 'Kecamatan', key: 'kecamatan', type: 'select', options: kecamatanOptions },
        { label: 'Provider', key: 'provider', type: 'select', options: providerOptions },
        { label: 'Status', key: 'status', type: 'select', options: [{ nama: 'Aktif' }, { nama: 'Tidak Aktif' }] },
        { label: 'Latitude', key: 'koordinat_lat', type: 'number' },
        { label: 'Longitude', key: 'koordinat_lng', type: 'number' }
      ],
      users: [
        { label: 'Username', key: 'username', type: 'text' },
        { label: 'Password', key: 'password', type: 'password' },
        { label: 'Role', key: 'role', type: 'select', options: [{ nama: 'admin' }, { nama: 'user' }] }
      ],
      jenis: [{ label: 'Nama Jenis', key: 'nama', type: 'text' }],
      operator: [{ label: 'Nama Operator', key: 'nama', type: 'text' }],
      kecamatan: [{ label: 'Nama Kecamatan', key: 'nama', type: 'text' }]
    };

    return (
      <div className="form-fields">
        {(fields[activeTab] || []).map((field, i) => (
          <div key={i} className="form-group">
            <label>{field.label}</label>
            {field.type === 'select' ? (
              <select
                value={formData[field.key] || ''}
                onChange={(e) => setFormData({ ...formData, [field.key]: e.target.value })}
              >
                <option value="">Pilih {field.label}</option>
                {(field.options || []).map((opt, j) => (
                  <option key={j} value={opt.nama}>{opt.nama}</option>
                ))}
              </select>
            ) : (
              <input
                type={field.type}
                step={field.type === 'number' ? '0.000001' : undefined}
                value={formData[field.key] || ''}
                onChange={(e) => setFormData({ 
                  ...formData, 
                  [field.key]: field.type === 'number' ? parseFloat(e.target.value) || '' : e.target.value 
                })}
                placeholder={`Masukkan ${field.label.toLowerCase()}`}
              />
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderConfirmDialog = () => {
    if (!confirmDialog) return null;

    return (
      <div className="confirm-overlay" onClick={confirmDialog.onCancel}>
        <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
          <div className="confirm-icon">⚠️</div>
          <h3>Konfirmasi</h3>
          <p>{confirmDialog.message}</p>
          <div className="confirm-actions">
            <button className="btn-secondary" onClick={confirmDialog.onCancel}>
              Batal
            </button>
            <button className="btn-danger" onClick={confirmDialog.onConfirm}>
              Hapus
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderPasswordResetModal = () => {
    if (!passwordResetResult) return null;

    const copyToClipboard = async () => {
      try {
        await navigator.clipboard.writeText(passwordResetResult.newPassword);
        showNotification('Password berhasil disalin ke clipboard', 'success');
        setPasswordResetResult(null); // Close modal after copying
      } catch (err) {
        showNotification('Gagal menyalin password', 'error');
      }
    };

    return (
      <div className="confirm-overlay" onClick={() => setPasswordResetResult(null)}>
        <div className="confirm-dialog password-reset-dialog" onClick={(e) => e.stopPropagation()}>
          <div className="confirm-icon">🔑</div>
          <h3>Password Berhasil Direset</h3>
          <div className="password-reset-content">
            <p><strong>Username:</strong> {passwordResetResult.username}</p>
            <div className="password-display">
              <label>Password Baru:</label>
              <div className="password-input-group">
                <input
                  type="text"
                  value={passwordResetResult.newPassword}
                  readOnly
                  className="password-input"
                />
                <button className="btn-copy" onClick={copyToClipboard} title="Salin Password">
                  📋
                </button>
              </div>
            </div>
            <p className="password-warning">
              ⚠️ Simpan password ini dengan aman. Password tidak akan ditampilkan lagi.
            </p>
          </div>
          <div className="confirm-actions">
            <button className="btn-primary" onClick={() => setPasswordResetResult(null)}>
              Tutup
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="admin-dashboard">
      {notification && (
        <div className={`notification ${notification.type}`}>
          {notification.type === 'success' ? '✓' : '✕'} {notification.message}
        </div>
      )}

      {renderConfirmDialog()}

      {renderPasswordResetModal()}

      <aside className="sidebar">
        <div className="sidebar-header">
          <h2>🗺️ KOORDITEL KABUPATEN MANGGARAI BARAT</h2>
          <p>Admin Panel</p>
          <button className="btn-logout" onClick={handleLogout} title="Logout">
            🚪 Logout
          </button>
        </div>
        <nav>
          {navigationItems.map(item => (
            <button
              key={item.id}
              className={`nav-item ${activeTab === item.id ? 'active' : ''}`}
              onClick={() => {
                setActiveTab(item.id);
                resetForm();
                setSearchTerm('');
              }}
            >
              <span className="nav-icon">{item.icon}</span>
              <span>{item.label}</span>
            </button>
          ))}
        </nav>
      </aside>

      <main className="main-content">
        {activeTab === 'dashboard' ? (
          renderDashboard()
        ) : (
          <div className="content-view">
            <div className="content-header">
              <h2>{navigationItems.find(n => n.id === activeTab)?.label}</h2>
              <div className="header-actions">
                <input
                  type="search"
                  className="search-input"
                  placeholder="Cari..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <button className="btn-primary" onClick={() => setShowForm(true)}>
                  ➕ Tambah
                </button>
              </div>
            </div>

            {activeTab === 'towers' && renderTable(filteredTowers, [
              { label: 'Nama Operator', key: 'nama_optel' },
              { label: 'Jenis', key: 'jenis' },
              { label: 'Lokasi', key: 'lokasi' },
              { label: 'Kecamatan', key: 'kecamatan' },
              { label: 'Tinggi', key: 'tinggi' },
              { 
                label: 'Status', 
                render: (item) => (
                  <span>{item.status}</span>
                )
              }
            ], handleEdit, handleDelete)}

            {activeTab === 'wifi' && renderTable(filteredWifi, [
              { label: 'Nama', key: 'nama' },
              { label: 'Lokasi', key: 'lokasi' },
              { label: 'Kecamatan', key: 'kecamatan' },
              { label: 'Provider', key: 'provider' },
              { 
                label: 'Status', 
                render: (item) => (
                  <span>{item.status}</span>
                )
              }
            ], handleEdit, handleDelete)}

            {activeTab === 'users' && renderTable(filteredUsers, [
              { label: 'Username', key: 'username' },
              {
                label: 'Role',
                render: (item) => (
                  <span>{item.role}</span>
                )
              },
              {
                label: 'Dibuat',
                render: (item) => new Date(item.created_at).toLocaleDateString('id-ID')
              }
            ], handleEdit, handleDelete, handleResetPassword)}

            {['jenis', 'operator', 'kecamatan'].includes(activeTab) && renderTable(
              activeTab === 'jenis' ? filteredJenis : activeTab === 'operator' ? filteredOperator : filteredKecamatan,
              [
                { label: 'Nama', key: 'nama' },
                { 
                  label: 'Dibuat', 
                  render: (item) => new Date(item.created_at).toLocaleDateString('id-ID')
                }
              ],
              handleEdit,
              handleDelete
            )}
          </div>
        )}

        {showForm && renderMapForm(['towers', 'wifi'].includes(activeTab))}
      </main>

      {loading && (
        <div className="loading-overlay">
          <div className="spinner"></div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;